import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Values = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardsRef = useRef(null);

  const studioValues = [
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9ff00" strokeWidth="1.5">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
        </svg>
      ),
      title: "Quality Over Quantity",
      description:
        "Every project gets full attention. We focus on delivering well-crafted work rather than taking on more than we can handle.",
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9ff00" strokeWidth="1.5">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      ),
      title: "Transparency",
      description:
        "Open communication at every stage. Clients always know where their project stands, what's next, and what trade-offs exist.",
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9ff00" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
      title: "Pragmatic Innovation",
      description:
        "We use modern tools and approaches, but only when they serve the project. Technology choices are driven by the problem, not trends.",
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9ff00" strokeWidth="1.5">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      title: "Partnership Mindset",
      description:
        "We work with clients, not just for them. The best results come from genuine collaboration and shared ownership of the outcome.",
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Heading animation
      gsap.from(headingRef.current, {
        y: 100,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Cards animation
      if (cardsRef.current) {
        const cards = cardsRef.current.querySelectorAll(".value-card");
        cards.forEach((card, index) => {
          gsap.from(card, {
            y: 80,
            opacity: 0,
            duration: 1,
            delay: index * 0.15,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          });
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="values"
      className="relative py-32 md:py-48 bg-[#0a0a0a] overflow-hidden"
    >
      {/* Background accent */}
      <div className="circle-accent bottom-0 left-0 -translate-x-1/2 translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="text-center mb-16 md:mb-24">
          <span className="section-label justify-center">Studio Values</span>
          <h2 ref={headingRef} className="section-title">
            How We<br />
            <span className="text-gradient">Work</span>
          </h2>
        </div>

        {/* Values grid */}
        <div
          ref={cardsRef}
          className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto"
        >
          {studioValues.map((value, index) => (
            <div
              key={index}
              className="value-card group p-8 md:p-10 border border-[#222] rounded-2xl hover:border-[#c9ff00] transition-all duration-500 hover:bg-[#111]"
            >
              <div className="w-16 h-16 rounded-full bg-[#111] group-hover:bg-[#1a1a1a] flex items-center justify-center mb-6 transition-colors duration-500">
                {value.icon}
              </div>
              <h3 className="text-xl md:text-2xl font-display font-semibold mb-4">
                {value.title}
              </h3>
              <p className="text-[#888] leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-8 mt-20 pt-12 border-t border-[#222] max-w-2xl mx-auto text-center">
          <div>
            <div className="text-3xl md:text-4xl font-display font-bold text-[#c9ff00]">
              11+
            </div>
            <div className="text-sm text-[#888] mt-2">Projects</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-display font-bold text-[#c9ff00]">
              4
            </div>
            <div className="text-sm text-[#888] mt-2">Services</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-display font-bold text-[#c9ff00]">
              100%
            </div>
            <div className="text-sm text-[#888] mt-2">Dedication</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Values;
